// src/app/services/laboratorio/laboratorio-storage.service.ts

import { Injectable } from '@angular/core';
import { ParametroLaboratorio } from '../../interfaces/laboratorio/parametro-laboratorio.interface';
import { ResultadoExtraido, ResultadoLaboratorio } from '../../interfaces/laboratorio/resultado-laboratorio.interface';
import {
  HistoricoLaboratorio,
  ParametroHistorico,
  ValorPorFecha,
} from '../../interfaces/laboratorio/historico-laboratorio.interface';
import { MOCK_PARAMETROS_TODOS } from '../../mock-data/laboratorio/parametros.mock';

@Injectable({
  providedIn: 'root',
})
export class LaboratorioStorageService {
  private readonly STORAGE_KEYS = {
    PARAMETROS: 'med_app_parametros_laboratorio',
    RESULTADOS: 'med_app_resultados_laboratorio',
  };

  constructor() {
    this.inicializarStorage();
  }

  /**
   * Inicializar localStorage con catálogo de parámetros
   */
  private inicializarStorage(): void {
    const parametros = localStorage.getItem(this.STORAGE_KEYS.PARAMETROS);

    if (!parametros) {
      localStorage.setItem(this.STORAGE_KEYS.PARAMETROS, JSON.stringify(MOCK_PARAMETROS_TODOS));
      console.log('✅ Catálogo inicializado:', MOCK_PARAMETROS_TODOS.length, 'parámetros');
    }

    const resultados = localStorage.getItem(this.STORAGE_KEYS.RESULTADOS);
    if (!resultados) {
      localStorage.setItem(this.STORAGE_KEYS.RESULTADOS, JSON.stringify([]));
      console.log('✅ Storage de resultados inicializado');
    }
  }

  // ==================== PARÁMETROS ====================

  /**
   * Obtener catálogo de parámetros
   */
  async getParametros(categoria?: string): Promise<ParametroLaboratorio[]> {
    await this.simulateDelay(300); // Simular latencia de red

    const parametros: ParametroLaboratorio[] = JSON.parse(localStorage.getItem(this.STORAGE_KEYS.PARAMETROS) || '[]');

    if (categoria) {
      return parametros.filter((p) => p.categoria === categoria);
    }

    return parametros;
  }

  // ==================== RESULTADOS ====================

  /**
   * Guardar resultados de laboratorio
   */
  async guardarResultados(
    appointmentId: number,
    paraclinicoUuid: string,
    resultados: ResultadoExtraido[],
  ): Promise<any> {
    await this.simulateDelay(500);

    const todosResultados: ResultadoLaboratorio[] = JSON.parse(
      localStorage.getItem(this.STORAGE_KEYS.RESULTADOS) || '[]',
    );

    const nuevosResultados: ResultadoLaboratorio[] = resultados.map((r) => ({
      id: Date.now() + Math.random(), // ID temporal
      appointmentId: appointmentId,
      paraclinicoUuid: paraclinicoUuid,
      parametroId: r.parametroId,
      valorNumerico: typeof r.valor === 'number' ? r.valor : undefined,
      valorTexto: typeof r.valor === 'string' ? r.valor : undefined,
      unidad: r.unidad,
      fueraRango: r.fueraRango || false,
      nivelAlerta: r.nivelAlerta,
      metodoExtraccion: 'automatico',
      confianza: r.confianza,
      textoOriginal: r.textoOriginal,
      editadoManualmente: false,
      createdAt: new Date().toISOString(),
    }));

    todosResultados.push(...nuevosResultados);
    localStorage.setItem(this.STORAGE_KEYS.RESULTADOS, JSON.stringify(todosResultados));

    console.log('✅ Guardados', nuevosResultados.length, 'resultados en localStorage');

    return {
      success: true,
      message: `${nuevosResultados.length} resultados guardados`,
      data: {
        appointmentId,
        paraclinicoUuid,
        totalResultados: nuevosResultados.length,
      },
    };
  }

  /**
   * Obtener historial de laboratorios de un paciente
   */
  async getHistorico(pacienteId: number, categoria?: string): Promise<HistoricoLaboratorio> {
    await this.simulateDelay(800);

    const todosResultados: ResultadoLaboratorio[] = JSON.parse(
      localStorage.getItem(this.STORAGE_KEYS.RESULTADOS) || '[]',
    );

    const parametros: ParametroLaboratorio[] = JSON.parse(localStorage.getItem(this.STORAGE_KEYS.PARAMETROS) || '[]');

    // Filtrar resultados del paciente (simulado por appointmentId)
    // En producción, el backend filtraría por pacienteId real
    let resultadosFiltrados = todosResultados;

    if (categoria) {
      const parametrosIds = parametros.filter((p) => p.categoria === categoria).map((p) => p.id);

      resultadosFiltrados = resultadosFiltrados.filter((r) => parametrosIds.includes(r.parametroId));
    }

    // Agrupar por fecha y parámetro
    const fechasUnicas = [...new Set(resultadosFiltrados.map((r) => r.createdAt?.split('T')[0] || ''))].sort();

    // Agrupar por parámetro
    const parametrosMap = new Map<number, ParametroHistorico>();

    resultadosFiltrados.forEach((resultado) => {
      const parametro = parametros.find((p) => p.id === resultado.parametroId);
      if (!parametro) return;

      if (!parametrosMap.has(resultado.parametroId)) {
        parametrosMap.set(resultado.parametroId, {
          parametroId: resultado.parametroId,
          parametroNombre: parametro.nombre,
          categoria: parametro.categoria,
          unidad: parametro.unidad,
          rangoMin: parametro.rangoMin,
          rangoMax: parametro.rangoMax,
          valores: [],
        });
      }

      const fecha = resultado.createdAt?.split('T')[0] || '';
      const valor: ValorPorFecha = {
        fecha: fecha,
        valor: resultado.valorNumerico || resultado.valorTexto || '',
        fueraRango: resultado.fueraRango,
        nivel: resultado.nivelAlerta,
        appointmentId: resultado.appointmentId,
      };

      parametrosMap.get(resultado.parametroId)!.valores.push(valor);
    });

    return {
      pacienteId: pacienteId,
      fechas: fechasUnicas,
      parametros: Array.from(parametrosMap.values()),
    };
  }

  /**
   * Obtener historial de un parámetro específico
   */
  async getHistoricoParametro(pacienteId: number, parametroId: number): Promise<any> {
    await this.simulateDelay(500);

    const todosResultados: ResultadoLaboratorio[] = JSON.parse(
      localStorage.getItem(this.STORAGE_KEYS.RESULTADOS) || '[]',
    );

    const parametros: ParametroLaboratorio[] = JSON.parse(localStorage.getItem(this.STORAGE_KEYS.PARAMETROS) || '[]');

    const parametro = parametros.find((p) => p.id === parametroId);
    const resultados = todosResultados.filter((r) => r.parametroId === parametroId);

    return {
      parametro: parametro
        ? {
            id: parametro.id,
            nombre: parametro.nombre,
            unidad: parametro.unidad,
            rangoMin: parametro.rangoMin,
            rangoMax: parametro.rangoMax,
          }
        : null,
      valores: resultados.map((r) => ({
        fecha: r.createdAt?.split('T')[0] || '',
        valor: r.valorNumerico || r.valorTexto,
        appointmentId: r.appointmentId,
      })),
    };
  }

  /**
   * Editar resultado individual
   */
  async editarResultado(resultadoId: number, datos: Partial<ResultadoLaboratorio>): Promise<any> {
    await this.simulateDelay(300);

    const todosResultados: ResultadoLaboratorio[] = JSON.parse(
      localStorage.getItem(this.STORAGE_KEYS.RESULTADOS) || '[]',
    );

    const index = todosResultados.findIndex((r) => r.id === resultadoId);

    if (index === -1) {
      throw new Error('Resultado no encontrado');
    }

    todosResultados[index] = {
      ...todosResultados[index],
      ...datos,
      editadoManualmente: true,
    };

    localStorage.setItem(this.STORAGE_KEYS.RESULTADOS, JSON.stringify(todosResultados));

    return {
      success: true,
      message: 'Resultado actualizado',
      data: todosResultados[index],
    };
  }

  // ==================== UTILIDADES ====================

  /**
   * Simular latencia de red
   */
  private simulateDelay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Limpiar todos los resultados (para testing)
   */
  async limpiarResultados(): Promise<void> {
    localStorage.setItem(this.STORAGE_KEYS.RESULTADOS, JSON.stringify([]));
    console.log('🗑️ Resultados limpiados');
  }

  /**
   * Reinicializar catálogo de parámetros
   */
  async reinicializarParametros(): Promise<void> {
    localStorage.setItem(this.STORAGE_KEYS.PARAMETROS, JSON.stringify(MOCK_PARAMETROS_TODOS));
    console.log('🔄 Catálogo reinicializado');
  }

  /**
   * Obtener estadísticas de storage
   */
  getEstadisticas(): any {
    const parametros: ParametroLaboratorio[] = JSON.parse(localStorage.getItem(this.STORAGE_KEYS.PARAMETROS) || '[]');

    const resultados: ResultadoLaboratorio[] = JSON.parse(localStorage.getItem(this.STORAGE_KEYS.RESULTADOS) || '[]');

    return {
      totalParametros: parametros.length,
      totalResultados: resultados.length,
      resultadosPorCategoria: this.contarPorCategoria(resultados, parametros),
      ultimoResultado: resultados[resultados.length - 1]?.createdAt || null,
    };
  }

  private contarPorCategoria(resultados: ResultadoLaboratorio[], parametros: ParametroLaboratorio[]): any {
    const counts: any = {};

    resultados.forEach((r) => {
      const parametro = parametros.find((p) => p.id === r.parametroId);
      if (parametro) {
        counts[parametro.categoria] = (counts[parametro.categoria] || 0) + 1;
      }
    });

    return counts;
  }
}
